<ng-container *vsLet="data$ | async as data">
  <ng-container *vsLet="canEditOrganization$ | async as canEditOrganization">
    <div>
      <!-- Organization Profile Section -->
      <div id="page-header" class="flex flex-col gap-1 pb-2">
        <h1 class="text-lg font-bold">Organization profile</h1>
        <p class="text-sm text-gray-700">Update your company photo and details here.</p>
      </div>
      <vs-divider class="flex my-4"></vs-divider>

      <div id="page-content" class="flex flex-col gap-4 pt-2 pb-8">
        <form [formGroup]="form" (keydown.enter)="$event.preventDefault()">
          <div>
            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{
              label: 'Organization name',
              hint: '',
              description: 'This will be across the administration portal.'
            }"
            >
              <input vsInput placeholder="VectorsUI" type="text" formControlName="brandName" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{
              label: 'Tagline',
              hint: '',
              description: 'This short description will be shown in headers or below your logo.'
            }"
            >
              <input vsInput placeholder="Your home inspection guarantee" type="text" formControlName="tagLine" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{
              label: 'Product name',
              hint: '',
              description: 'This product name will be used in emails and other descriptions of your company’s services.'
            }"
            >
              <input vsInput placeholder="Protect" type="text" formControlName="productName" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{ label: 'Contact email', description: 'This will be across the administration and customer portals.' }"
            >
              <input vsInput required placeholder="address@email.com" type="text" formControlName="contactEmail" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{ label: 'Contact phone', description: 'This will be across the administration and customer portals.' }"
            >
              <input vsInput required placeholder="+1 (111) 111-1111" type="text" formControlName="contactPhone" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{ label: 'Contact address', description: 'This will be across the administration and customer portals.' }"
            >
              <vs-address-input required formControlName="contactAddress"></vs-address-input>
            </vs-form-field>

            <div class="pt-6">
              <h2 class="text-base font-semibold text-gray-900">Email Configuration</h2>
              <p class="text-sm text-gray-600 mt-1">
                Configure tenant-wide sender and reply settings used by transactional emails.
              </p>
            </div>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{ label: 'From name', description: 'Display name used in outbound emails.' }"
            >
              <input vsInput required placeholder="Customer Care Team" type="text" formControlName="fromName" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{ label: 'From email', description: 'Email address shown as the sender.' }"
            >
              <input vsInput required placeholder="no-reply@example.com" type="text" formControlName="fromEmail" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{ label: 'Reply-to name', description: 'Display name for reply-to messages.' }"
            >
              <input vsInput required placeholder="Support Team" type="text" formControlName="replyToName" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{ label: 'Reply-to email', description: 'Destination address for customer replies.' }"
            >
              <input vsInput required placeholder="support@example.com" type="text" formControlName="replyToEmail" />
            </vs-form-field>

            <vs-form-field
              theme="primary"
              class="pt-4 pb-4"
              appearance="responsive"
              [config]="{ label: 'Product team name', description: 'Team name shown in email sign-offs when enabled.' }"
            >
              <input vsInput required placeholder="Customer Care Team" type="text" formControlName="productTeam" />
            </vs-form-field>
          </div>

          <div
            class="flex flex-col-reverse sm:flex-row sm:justify-end items-center gap-3 pb-10"
            *ngIf="canEditOrganization&&form.dirty"
          >
            <button
              vs-button
              class="sm:flex-none w-full sm:w-fit"
              appearance="outline"
              [disabled]="!form.dirty"
              (click)="resetForm()"
            >
              Cancel
            </button>

            <button
              vs-button
              class="sm:flex-none w-full sm:w-fit"
              theme="primary"
              [disabled]="!canEditOrganization || !inputIsValid() || !form.dirty"
              (click)="confirm()"
            >
              Save
            </button>
          </div>
        </form>
      </div>

      <ws-customer-portal-details
        [config]="portalConfigDetail"
        (refresh)="refreshPage()"
        [staff]="data?.staff"
        [canEditOrganization]="canEditOrganization || false"
      ></ws-customer-portal-details>

      <!-- Portal Settings Section -->
      <div class="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mt-8 mb-4">
        <div class="flex flex-col">
          <h1 class="text-lg font-semibold">Portal Settings</h1>
          <p class="text-sm text-gray-600">Manage your customer portal options here.</p>
        </div>
      </div>

      <form class="flex flex-col gap-4" [formGroup]="portalForm">
        <div class="flex">
          <label
            class="flex items-center gap-4 cursor-pointer"
            tooltipPosition="end"
            [vsTooltip]="!canEditOrganization ? 'Insufficient permissions to update settings':''"
          >
            <input
              vsInput
              type="checkbox"
              appearance="toggle"
              formControlName="loginDisabled"
              [disabled]="!canEditOrganization"
              (change)="portalForm.markAsDirty()"
            />
            <div class="text-sm">
              <p class="text-gray-700 font-medium">Disallow Customer Portal Login</p>
              <p class="text-gray-600">Disable customers from logging into the customer portal.</p>
            </div>
          </label>
        </div>

        <div class="flex">
          <label
            class="flex items-center gap-4 cursor-pointer"
            tooltipPosition="end"
            [vsTooltip]="!canEditOrganization ? 'Insufficient permissions to update settings':''"
          >
            <input
              vsInput
              type="checkbox"
              appearance="toggle"
              formControlName="salesDisabled"
              [disabled]="!canEditOrganization"
              (change)="portalForm.markAsDirty()"
            />
            <div class="text-sm">
              <p class="text-gray-700 font-medium">Disallow Portal Sales</p>
              <p class="text-gray-600">Disable customers from making purchases through the customer portal.</p>
            </div>
          </label>
        </div>

        <!-- Promo Text Section -->
        <div class="flex flex-col gap-2 mt-6">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
            <!-- Left column: label, description, example -->
            <div class="flex flex-col gap-2 sm:col-span-1">
              <label class="text-sm font-medium text-gray-700">Promo Text</label>
              <p class="text-xs text-gray-600 mb-1">
                This text will appear in the left panel of the Customer Portal. Use it to highlight promotions,
                instructions, or other important information for your customers.
              </p>
            </div>
            <!-- Right column: promo text display/edit -->
            <div class="flex flex-col gap-2 sm:col-span-2">
              <textarea
                vsInput
                rows="3"
                class="cursor-text resize-none"
                [placeholder]="'Click to add promo text...'"
                [value]="promoTextPlain"
                (click)="openPromoTextDialog()"
                (keydown)="onPromoTextKeydown($event)"
              ></textarea>
            </div>
          </div>
        </div>
        <!-- End Promo Text Section -->

        <ws-edit-promo-text-dialog
          #promoTextDialog
          [value]="portalForm.value.promoText"
          (saved)="onPromoTextSaved($event)"
        ></ws-edit-promo-text-dialog>

        <!-- Claims Message Section -->
        <div class="flex flex-col gap-2 mt-6">
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
            <div class="flex flex-col gap-2 sm:col-span-1">
              <label class="text-sm font-medium text-gray-700">Claims Message</label>
              <p class="text-xs text-gray-600 mb-1">
                This text will appear in place of the login on the Customer Portal when unauthenticated users visit the
                claims page directly via the URL. Use it to provide instructions or other information for your customers
                regarding submitting claims or inquiring about claims status.
              </p>
            </div>
            <div class="flex flex-col gap-2 sm:col-span-2">
              <textarea
                vsInput
                rows="3"
                class="cursor-text resize-none"
                [placeholder]="'Click to add claims message...'"
                [value]="claimsMessagePlain"
                (click)="openClaimsMessageDialog()"
                (keydown)="onClaimsMessageKeydown($event)"
              ></textarea>
            </div>
          </div>
        </div>
        <!-- End Claims Message Section -->

        <ws-edit-claims-message-dialog
          #claimsMessageDialog
          [value]="portalForm.value.claimsMessage"
          (saved)="onClaimsMessageSaved($event)"
        ></ws-edit-claims-message-dialog>

        <!-- Save and Cancel buttons for Portal Settings -->
        <div
          class="flex flex-col-reverse sm:flex-row sm:justify-end items-center gap-3 pb-10"
          *ngIf="canEditOrganization&&portalForm.dirty"
        >
          <button
            vs-button
            class="sm:flex-none w-full sm:w-fit"
            appearance="outline"
            [disabled]="!portalForm.dirty"
            (click)="resetPortalForm()"
          >
            Cancel
          </button>

          <button
            vs-button
            class="sm:flex-none w-full sm:w-fit"
            theme="primary"
            [disabled]="!canEditOrganization || !portalForm.dirty || portalSaving"
            (click)="savePortal()"
          >
            Save
          </button>
        </div>
      </form>

      <!-- Distributor Settings Section -->
      <div class="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mt-8 mb-4">
        <div class="flex justify-between w-full">
          <div class="flex flex-col">
            <h1 class="text-lg font-semibold">Distributor Settings</h1>
            <p class="text-sm text-gray-600">Manage your distributor options here.</p>
          </div>

          <button
            *ngIf="distributorForm.dirty"
            vs-button
            theme="primary"
            [disabled]="distributorSaving || (distributorForm.value.selfServeInvoicePackage?.enabled && distributorForm.controls['selfServeInvoicePackage'].invalid)"
            (click)="saveDistributor()"
          >
            Save
          </button>
        </div>
      </div>

      <!-- Distributor Form (should NOT be a nested <form>) -->
      <div class="flex flex-col gap-4" [formGroup]="distributorForm">
        <vs-card
          formGroupName="selfServeInvoicePackage"
          [class]="(distributorForm.value.selfServeInvoicePackage?.enabled) ? 'transition-all' : 'transition-all !bg-transparent !border-transparent !shadow-transparent'"
        >
          <vs-card-header
            [class]="(distributorForm.value.selfServeInvoicePackage?.enabled || !canEditOrganization) ? 'transition-all' : 'transition-all !p-0 !border-transparent'"
          >
            <div class="flex">
              <label
                class="flex items-center gap-4 cursor-pointer"
                tooltipPosition="end"
                [vsTooltip]="!canEditOrganization ? 'Insufficient permissions to update settings':''"
              >
                <input
                  vsInput
                  type="checkbox"
                  appearance="toggle"
                  formControlName="enabled"
                  [disabled]="!canEditOrganization"
                />

                <div class="text-sm">
                  <p class="text-gray-700 font-medium">Self-Serve Invoice Packages</p>
                  <p class="text-gray-600">
                    Enable distributors to self-serve retrieve their monthly invoice packages directly from the
                    dashboard.
                  </p>
                </div>
              </label>
            </div>
          </vs-card-header>

          <vs-card-content
            class="transition-all overflow-hidden [&>vs-form-field]:mb-6"
            [class.h-0]="!distributorForm.value.selfServeInvoicePackage?.enabled || !canEditOrganization"
            [class.!p-0]="!distributorForm.value.selfServeInvoicePackage?.enabled || !canEditOrganization"
          >
            <vs-form-field
              [config]="{ label: 'Invoice logo', description: 'Logo displayed at the top-left of the invoice.' }"
            >
              <div
                *ngIf="logoInput.files?.length || distiSettings?.selfServeInvoicePackage?.logo"
                class="flex flex-col gap-1"
                slot="before-content"
              >
                <img
                  class="aspect-square hover:object-contain"
                  [style.height]="logoInput.offsetHeight + 'px'"
                  [vsImagePreview]="logoInput.files?.[0] ?? distiSettings?.selfServeInvoicePackage?.logo ?? null"
                />
              </div>
              <input #logoInput vsInput type="file" formControlName="logo" accept=".SVG, .PNG, .JPG, .GIF" />
            </vs-form-field>

            <!-- Company name -->
            <vs-form-field
              [config]="{ label: 'Company Name', description: 'Name of the company/entity that will collect payments from distributors.' }"
            >
              <vs-input-container>
                <input required type="text" placeholder="Company name" formControlName="companyName" />
                <vs-icon slot="end" name="building-07"></vs-icon>
              </vs-input-container>
            </vs-form-field>

            <!-- Main place of business -->
            <vs-form-field
              [config]="{ label: 'Main Place of Business', description: 'Address/location of the main place of business of the company/entity collecting payments from distributors.' }"
            >
              <textarea
                vsInput
                required
                rows="3"
                placeholder="Company name / Street address / City, State ZIP"
                formControlName="companyAddress"
              ></textarea>
            </vs-form-field>

            <!-- Banking details -->
            <vs-form-field
              [config]="{ label: 'Banking Details', description: 'Account details to be included in all invoices as the destination for direct deposit payments.' }"
            >
              <vs-input-group class="grid grid-cols-2" [skipMargin]="true" formGroupName="bankingDetails">
                <input
                  vsInput
                  type="text"
                  formControlName="bank"
                  placeholder="Bank name"
                  style="width: calc(100% - 1px)"
                  class="col-span-2 !rounded-t-md !rounded-b-none"
                />
                <input
                  #accountNumberInput
                  vsInput
                  min="1"
                  step="1"
                  type="number"
                  placeholder="Account number"
                  formControlName="accountNumber"
                  class="-mt-px !rounded-none !rounded-bl-md"
                  (change)="accountNumberInput.value = accountNumberInput.value.replace('.', '')"
                />
                <input
                  #routingNumberInput
                  vsInput
                  min="1"
                  step="1"
                  type="number"
                  formControlName="routingNumber"
                  placeholder="Wire routing number"
                  class="-mt-px -ml-px !rounded-none !rounded-br-md"
                  (change)="routingNumberInput.value = routingNumberInput.value.replace('.', '')"
                />
              </vs-input-group>
            </vs-form-field>

            <!-- Unit price -->
            <vs-form-field
              [config]="{ label: 'Unit Price', description: 'Cost charged for each warranty sold, configurable per policy plan.' }"
            >
              <div *ngIf="distributorForm.value.selfServeInvoicePackage?.enabled">
                <div class="flex flex-col gap-3">
                  <vs-form-field
                    [config]="{ label: 'Search', description: 'Search by policy title/number or plan title.' }"
                  >
                    <input
                      vsInput
                      type="text"
                      [value]="unitPriceSearch"
                      placeholder="Search plans..."
                      (input)="onUnitPriceSearchChange($any($event.target).value)"
                    />
                  </vs-form-field>

                  <div formArrayName="unitPrices">
                    <div *ngFor="let row of unitPriceRows; let last = last" class="mb-2">
                      <vs-input-group class="grid grid-cols-2" [skipMargin]="true">
                        <div vsInputGroupItem class="!px-0 relative overflow-hidden rounded-none">
                          <div class="flex flex-col px-3 py-2">
                            <p class="truncate text-xs font-semibold text-gray-700 w-full">
                              {{ row.policyTitle }}
                              <span class="text-gray-500 font-normal">({{ row.policyNumber }})</span>
                            </p>
                            <p class="truncate text-sm text-gray-700">{{ row.planTitle }}</p>
                          </div>
                        </div>

                        <ng-container *ngIf="getControlIndex(row.planId) as controlIndex; else pending">
                          <ng-container [formGroupName]="controlIndex.index">
                            <input
                              vsInput
                              currencyMask
                              type="text"
                              formControlName="price"
                              placeholder="Unit price (USD)"
                              class="-ml-px rounded-none"
                              [class.!rounded-br-md]="last"
                              [options]="currencyMaskConfig"
                              (blur)="onUnitPriceBlur(row.planId)"
                            />
                          </ng-container>
                        </ng-container>

                        <ng-template #pending>
                          <input
                            vsInput
                            readonly
                            type="number"
                            placeholder="Unit price (USD)"
                            class="-ml-px rounded-none"
                            [class.!rounded-br-md]="last"
                          />
                        </ng-template>
                      </vs-input-group>
                    </div>
                  </div>

                  <vs-pagination
                    [config]="{
                      pageSizes: [5, 10, 20, 50],
                      currentPage: unitPricePage,
                      itemsPerPage: unitPriceLimit,
                      totalItems: unitPriceTotalItems
                    }"
                    (pagination)="onUnitPricePaginationChange($event)"
                  ></vs-pagination>
                </div>
              </div>
            </vs-form-field>
          </vs-card-content>
        </vs-card>

        <div class="flex">
          <label
            class="flex items-center gap-4 cursor-pointer"
            tooltipPosition="end"
            [vsTooltip]="!canEditOrganization ? 'Insufficient permissions to update settings':''"
          >
            <input
              vsInput
              type="checkbox"
              appearance="toggle"
              formControlName="userRegistration"
              [disabled]="!canEditOrganization"
            />

            <div class="text-sm">
              <p class="text-gray-700 font-medium">Member registration</p>
              <p class="text-gray-600">
                Allow an initial member to register on the dashboard during the distributor onboarding process.
              </p>
            </div>
          </label>
        </div>
      </div>
      <!-- End distributorForm group -->

      <ws-claim-resolution-settings [canEdit]="canEditOrganization || false"></ws-claim-resolution-settings>
      <ws-claim-approval-settings [canEdit]="canEditOrganization || false"></ws-claim-approval-settings>
    </div>
  </ng-container>
</ng-container>
